import { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { chooseLandingPath } from "@/lib/auth-routing";
import { Sentry } from "@/lib/sentry";

/**
 * If the just-authenticated user has a verified TOTP factor, route them to
 * /mfa-verify before the dashboard. Returns true if a redirect was issued so
 * the caller can short-circuit. Fails closed — listFactors errors are treated
 * as "do not let them in" rather than letting OAuth users skip MFA silently.
 */
async function redirectIfMfaRequired(navigate: (path: string, opts?: { replace?: boolean }) => void): Promise<boolean> {
  try {
    const { data: factors, error: factorsErr } = await supabase.auth.mfa.listFactors();
    if (factorsErr) throw factorsErr;
    const hasVerifiedTotp = (factors?.totp ?? []).some(f => f.status === "verified");
    if (hasVerifiedTotp) {
      navigate("/mfa-verify", { replace: true });
      return true;
    }
    return false;
  } catch (e) {
    console.error("MFA factor check failed during auth callback:", e);
    await supabase.auth.signOut().catch(() => {});
    navigate("/login", { replace: true });
    return true;
  }
}

export default function AuthCallback() {
  useDocumentTitle("Authenticating");
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const handleCallback = async () => {
      try {
        const code = new URLSearchParams(window.location.search).get("code");
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) { setError(exchangeError.message); return; }
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) { setError(sessionError.message); return; }

        if (session?.user) {
          // Check both owner and team-member paths so users invited to an
          // existing company don't trigger duplicate company creation.
          const { data: ownedCompany } = await supabase
            .from("companies").select("id").eq("owner_user_id", session.user.id).maybeSingle();
          let existingCompany = ownedCompany;
          if (!existingCompany) {
            const { data: roleRow } = await supabase
              .from("company_roles")
              .select("company_id")
              .eq("user_id", session.user.id)
              .not("accepted_at", "is", null)
              .order("accepted_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (roleRow?.company_id) existingCompany = { id: roleRow.company_id };
          }

          if (!existingCompany) {
            const pendingCompany = localStorage.getItem("cr-pending-signup-company");
            const pendingReferral = localStorage.getItem("cr-pending-referral-code");
            const pendingCaBrokerRaw = localStorage.getItem("cr-pending-ca-broker");
            const companyName =
              session.user.user_metadata?.company_name || pendingCompany || "My Company";
            const vertical = session.user.user_metadata?.vertical || "freight";
            const metaRole = session.user.user_metadata?.logistics_role as string | undefined;
            const logisticsRole = metaRole || "dispatcher";
            // CA broker payload: localStorage primary, user_metadata fallback.
            // Parse defensively so bad JSON never blocks signup.
            let caBrokerData: Record<string, unknown> | undefined;
            if (pendingCaBrokerRaw) {
              try {
                const parsed = JSON.parse(pendingCaBrokerRaw);
                if (parsed && typeof parsed === "object") caBrokerData = parsed;
              } catch { /* ignore */ }
            }
            if (!caBrokerData && session.user.user_metadata?.ca_broker_data) {
              caBrokerData = session.user.user_metadata.ca_broker_data as Record<string, unknown>;
            }
            const { error: setupError } = await supabase.functions.invoke("setup-account", {
              body: {
                user_id: session.user.id,
                company_name: companyName,
                vertical,
                logistics_role: logisticsRole,
                ...(pendingReferral ? { referral_code: pendingReferral } : {}),
                ...(caBrokerData ? { country: "CA", ca_broker_data: caBrokerData } : {}),
              },
            });
            if (setupError) {
              setError(`Could not finish account setup: ${setupError.message ?? "unknown error"}. Try signing in again, or contact support@cipherandrow.com.`);
              return;
            }
            localStorage.removeItem("cr-pending-signup-company");
            localStorage.removeItem("cr-pending-referral-code");
            localStorage.removeItem("cr-pending-ca-broker");
          }

          // Accept carrier invite if stored
          const pendingInvite = localStorage.getItem("cr-invite-token");
          const inviteExpiry = localStorage.getItem("cr-invite-token-expiry");
          if (pendingInvite) {
            localStorage.removeItem("cr-invite-token");
            localStorage.removeItem("cr-invite-token-expiry");
            const isExpired = inviteExpiry && Date.now() > Number(inviteExpiry);
            if (!isExpired) {
              await supabase.functions.invoke("carrier-invite", {
                body: { action: "accept", token: pendingInvite },
              }).catch((e) => {
                Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
                  level: "warning",
                  tags: { source: "carrier-invite.accept", flow: "email" },
                });
              });
            }
          }

          if (await redirectIfMfaRequired(navigate)) return;

          // cr-needs-role-pick is set by Signup.tsx (email + Google OAuth paths)
          // when the user comes from the generic /signup page. It survives the
          // email verification round-trip because it's stored in the same browser.
          // Carrier/broker dedicated signup pages never set it, so those users
          // skip the picker and go straight to chooseLandingPath.
          const needsRolePick = localStorage.getItem("cr-needs-role-pick");
          if (needsRolePick) {
            localStorage.removeItem("cr-needs-role-pick");
            navigate("/signup/choose-role", { replace: true });
            return;
          }

          const role = session.user.user_metadata?.logistics_role ?? null;
          const path = await chooseLandingPath(session.user.id, role);
          navigate(path, { replace: true });
        } else {
          const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, sess) => {
            if (event === "SIGNED_IN" && sess?.user) {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              subscription.unsubscribe();
              subscriptionRef.current = null;
              if (cancelledRef.current) return;

              const { data: ownedExisting } = await supabase
                .from("companies").select("id").eq("owner_user_id", sess.user.id).maybeSingle();
              let existing = ownedExisting;
              if (!existing) {
                const { data: roleRow } = await supabase
                  .from("company_roles")
                  .select("company_id")
                  .eq("user_id", sess.user.id)
                  .not("accepted_at", "is", null)
                  .order("accepted_at", { ascending: false })
                  .limit(1)
                  .maybeSingle();
                if (roleRow?.company_id) existing = { id: roleRow.company_id };
              }

              if (!existing) {
                const pendingCompany = localStorage.getItem("cr-pending-signup-company");
                const pendingReferral = localStorage.getItem("cr-pending-referral-code");
                const pendingCaBrokerRaw = localStorage.getItem("cr-pending-ca-broker");
                const companyName =
                  sess.user.user_metadata?.company_name || pendingCompany || "My Company";
                const vertical = sess.user.user_metadata?.vertical || "freight";
                const metaOauthRole = sess.user.user_metadata?.logistics_role as string | undefined;
                const oauthRole = metaOauthRole || "dispatcher";
                let caBrokerData: Record<string, unknown> | undefined;
                if (pendingCaBrokerRaw) {
                  try {
                    const parsed = JSON.parse(pendingCaBrokerRaw);
                    if (parsed && typeof parsed === "object") caBrokerData = parsed;
                  } catch { /* ignore */ }
                }
                if (!caBrokerData && sess.user.user_metadata?.ca_broker_data) {
                  caBrokerData = sess.user.user_metadata.ca_broker_data as Record<string, unknown>;
                }
                const { error: setupError } = await supabase.functions.invoke("setup-account", {
                  body: {
                    user_id: sess.user.id,
                    company_name: companyName,
                    vertical,
                    logistics_role: oauthRole,
                    ...(pendingReferral ? { referral_code: pendingReferral } : {}),
                    ...(caBrokerData ? { country: "CA", ca_broker_data: caBrokerData } : {}),
                  },
                });
                if (setupError) {
                  setError(`Could not finish account setup: ${setupError.message ?? "unknown error"}. Try signing in again, or contact support@cipherandrow.com.`);
                  return;
                }
                localStorage.removeItem("cr-pending-signup-company");
                localStorage.removeItem("cr-pending-referral-code");
                localStorage.removeItem("cr-pending-ca-broker");
              }

              const oauthInvite = localStorage.getItem("cr-invite-token");
              const oauthInviteExpiry = localStorage.getItem("cr-invite-token-expiry");
              if (oauthInvite) {
                localStorage.removeItem("cr-invite-token");
                localStorage.removeItem("cr-invite-token-expiry");
                const isExpired = oauthInviteExpiry && Date.now() > Number(oauthInviteExpiry);
                if (!isExpired) {
                  await supabase.functions.invoke("carrier-invite", {
                    body: { action: "accept", token: oauthInvite },
                  }).catch((e) => {
                    Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
                      level: "warning",
                      tags: { source: "carrier-invite.accept", flow: "oauth" },
                    });
                  });
                }
              }

              if (await redirectIfMfaRequired(navigate)) return;

              const oauthNeedsRolePick = localStorage.getItem("cr-needs-role-pick");
              if (oauthNeedsRolePick) {
                localStorage.removeItem("cr-needs-role-pick");
                navigate("/signup/choose-role", { replace: true });
                return;
              }

              const oauthRoleForRedirect =
                sess.user.user_metadata?.logistics_role ?? null;
              const oauthPath = await chooseLandingPath(sess.user.id, oauthRoleForRedirect);
              navigate(oauthPath, { replace: true });
            }
          });
          subscriptionRef.current = subscription;

          timeoutRef.current = setTimeout(() => {
            if (cancelledRef.current) return;
            setError("Verification timed out. Please try signing in.");
          }, 10000);
        }
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        setError(err instanceof Error ? err.message : "Something went wrong during verification.");
      }
    };

    handleCallback();
    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-sm">
          <h1 className="text-2xl font-display text-foreground">Verification Failed</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex flex-col gap-2">
            <a href="/login" className="text-primary text-sm hover:underline">Go to Sign In →</a>
            <a href="/signup" className="text-primary text-sm hover:underline">Sign up with a business email →</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-background flex items-center justify-center">
      <div className="text-center space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Verifying your account…</p>
      </div>
    </div>
  );
}
